import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, ChevronDown, ChevronUp, Filter } from 'lucide-react';
import ModuleContainer from '../../layout/ModuleContainer.jsx';
import InfoButton from '../../common/InfoButton.jsx';

const STATUS_STYLE = {
  ACTIVE:  'bg-korinth-warning/10 text-korinth-warning',
  TP1_HIT: 'bg-korinth-accent/10 text-korinth-accent',
  TP2_HIT: 'bg-green-400/10 text-green-400',
  TP3_HIT: 'bg-yellow-400/10 text-yellow-400',
  STOPPED: 'bg-korinth-danger/10 text-korinth-danger',
  EXPIRED: 'bg-korinth-muted/10 text-korinth-muted',
  FILLED:  'bg-blue-500/10 text-blue-400',
};

function ScoreBreakdownRow({ label, value, max = 2, info }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-mono text-korinth-muted w-20 flex items-center">
        {label}
        {info && <InfoButton title={label}>{info}</InfoButton>}
      </span>
      <div className="flex-1 h-1.5 bg-korinth-bg rounded-full overflow-hidden">
        <div className="h-full bg-korinth-accent rounded-full" style={{ width: `${(value / max) * 100}%` }} />
      </div>
      <span className="text-[10px] font-mono text-korinth-text w-6 text-right">{value}/{max}</span>
    </div>
  );
}

export default function SignalsTab({ instrument }) {
  const [signals, setSignals] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState({ direction: 'all', minScore: 0 });

  useEffect(() => {
    async function fetchHistory() {
      try {
        const res = await fetch(`/api/futures-signals/history?instrument=${instrument}&limit=50`);
        if (res.ok) setSignals(await res.json());
      } catch { /* silent */ }
    }
    fetchHistory();
    const interval = setInterval(fetchHistory, 30000);
    return () => clearInterval(interval);
  }, [instrument]);

  const filtered = signals.filter(s => {
    if (filter.direction !== 'all' && s.direction !== filter.direction) return false;
    if (s.score < filter.minScore) return false;
    return true;
  });

  return (
    <ModuleContainer title="Signal History" subtitle="All past and present signals with full analysis">
      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Filter className="w-3 h-3 text-korinth-muted" />
        <select value={filter.direction} onChange={e => setFilter(f => ({ ...f, direction: e.target.value }))}
          className="bg-korinth-bg border border-korinth-border rounded px-2 py-1 text-[10px] font-mono text-korinth-text">
          <option value="all">All Directions</option>
          <option value="LONG">LONG only</option>
          <option value="SHORT">SHORT only</option>
        </select>
        <label className="text-[10px] font-mono text-korinth-muted flex items-center gap-1">
          Min Score:
          <input type="range" min="0" max="10" value={filter.minScore}
            onChange={e => setFilter(f => ({ ...f, minScore: parseInt(e.target.value) }))}
            className="w-20 h-1" />
          <span className="text-korinth-accent w-4">{filter.minScore}</span>
        </label>
        <InfoButton title="Signal Scoring">
          <p>Each signal is scored 0-10 based on 5 criteria (2 points each):</p>
          <p><b>HTF Bias</b> — regime alignment with trade direction</p>
          <p><b>Profile</b> — NY session classification confidence</p>
          <p><b>OTE Zone</b> — price in the fibonacci golden zone (0.62-0.79)</p>
          <p><b>FVG</b> — Fair Value Gap confirming entry zone</p>
          <p><b>Calendar</b> — no high-impact news imminent</p>
          <p>Signals fire when score reaches 7/10 or higher.</p>
        </InfoButton>
      </div>

      {/* Signal list */}
      <div className="space-y-2 max-h-[600px] overflow-y-auto">
        {filtered.length > 0 ? filtered.map((s, i) => {
          const isExpanded = expanded === s.id;
          const breakdown = s.score_breakdown || {};
          return (
            <div key={s.id || i} className={`bg-korinth-bg rounded-lg border ${
              s.status === 'ACTIVE' ? 'border-korinth-warning/40' :
              s.status?.includes('TP') ? 'border-korinth-accent/30' :
              s.status === 'STOPPED' ? 'border-korinth-danger/30' :
              'border-korinth-border/30'
            }`}>
              {/* Summary row */}
              <button onClick={() => setExpanded(isExpanded ? null : s.id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left">
                {s.direction === 'LONG'
                  ? <TrendingUp className="w-4 h-4 text-korinth-accent flex-shrink-0" />
                  : <TrendingDown className="w-4 h-4 text-korinth-danger flex-shrink-0" />}
                <span className={`text-xs font-mono font-bold w-10 ${s.direction === 'LONG' ? 'text-korinth-accent' : 'text-korinth-danger'}`}>
                  {s.direction}
                </span>
                <span className="text-xs font-mono text-white">{s.entry_price?.toFixed(2)}</span>
                <span className="text-[10px] font-mono text-korinth-muted">SL {s.stop_loss?.toFixed(2)}</span>
                <span className="text-xs font-mono text-korinth-accent ml-auto">{s.score}/10</span>
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${STATUS_STYLE[s.status] || ''}`}>{s.status}</span>
                {isExpanded ? <ChevronUp className="w-3 h-3 text-korinth-muted" /> : <ChevronDown className="w-3 h-3 text-korinth-muted" />}
              </button>

              {/* Expanded detail */}
              {isExpanded && (
                <div className="px-4 pb-3 border-t border-korinth-border/30 pt-3 space-y-3">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><div className="text-[10px] text-korinth-muted">Entry</div><div className="text-xs font-mono text-white">{s.entry_price?.toFixed(2)}</div></div>
                    <div><div className="text-[10px] text-korinth-muted">Stop Loss</div><div className="text-xs font-mono text-korinth-danger">{s.stop_loss?.toFixed(2)}</div></div>
                    <div><div className="text-[10px] text-korinth-muted">TP1 (1:1)</div><div className="text-xs font-mono text-korinth-accent">{s.take_profit_1?.toFixed(2)}</div></div>
                  </div>
                  {s.ny_profile && (
                    <div className="text-[10px] font-mono text-korinth-muted">
                      NY Profile: <span className="text-korinth-text">{s.ny_profile}</span>
                      {s.profile_confidence && <span> ({s.profile_confidence.toFixed(0)}% confidence)</span>}
                    </div>
                  )}
                  <div className="space-y-1">
                    <ScoreBreakdownRow label="HTF Bias" value={breakdown.htf_bias || 0} info="Does the current market regime support this trade direction?" />
                    <ScoreBreakdownRow label="Profile" value={breakdown.profile_confidence || 0} info="How confident is the NY session classification (Continuation/Reversal/etc.)?" />
                    <ScoreBreakdownRow label="OTE Zone" value={breakdown.ote_zone || 0} info="Is price in the fibonacci golden zone (0.62-0.79 retracement)?" />
                    <ScoreBreakdownRow label="FVG" value={breakdown.fvg_confluence || 0} info="Is there a Fair Value Gap confirming the entry zone?" />
                    <ScoreBreakdownRow label="Calendar" value={breakdown.calendar_clear || 0} info="Are there high-impact news events that could invalidate the setup?" />
                  </div>
                  <div className="text-[9px] text-korinth-muted font-mono">
                    {s.created_at && new Date(s.created_at).toLocaleString('en-US', { timeZone: 'Asia/Dubai' })} Dubai
                  </div>
                </div>
              )}
            </div>
          );
        }) : (
          <p className="text-xs text-korinth-muted font-mono text-center py-8">No signals match filters</p>
        )}
      </div>
    </ModuleContainer>
  );
}
